import { useEffect, useState } from 'react';
import { Button, Input, Label, Modal } from '@/components/ui';

/**
 * Destructive confirm dialog gated on typing the exact `expectedName`.
 * The confirm button stays disabled until the input matches.
 */
export function TypeNameConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  expectedName,
  confirmText = 'Delete',
  isLoading = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  expectedName: string;
  confirmText?: string;
  isLoading?: boolean;
}) {
  const [typed, setTyped] = useState('');

  // Reset the input every time the dialog opens so stale text from a
  // previous open doesn't auto-enable the confirm button.
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  const matches = typed === expectedName;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={!matches || isLoading}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{message}</p>
        <div className="space-y-2">
          <Label htmlFor="confirm-typename">
            Type <span className="font-mono text-foreground">{expectedName}</span> to confirm
          </Label>
          <Input
            id="confirm-typename"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>
    </Modal>
  );
}

export default TypeNameConfirmDialog;
